/**
 * LocalStorage Cache Utilities with TTL (Time To Live)
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

/**
 * Save data to localStorage with TTL
 */
export const setCache = <T>(key: string, data: T, ttlMinutes: number = 30): void => {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000, // Convert minutes to milliseconds
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Get data from localStorage if not expired
 */
export const getCache = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const cacheItem: CacheItem<T> = JSON.parse(item);
    const now = Date.now();
    const age = now - cacheItem.timestamp;

    // Check if cache has expired
    if (age > cacheItem.ttl) {
      localStorage.removeItem(key);
      return null;
    }

    return cacheItem.data;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

/**
 * Remove specific item from cache
 */
export const removeCache = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

/**
 * Clear all cache items
 */
export const clearAllCache = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Clear expired cache items
 */
export const clearExpiredCache = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();

    keys.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item) {
        try {
          const cacheItem: CacheItem<any> = JSON.parse(item);
          const age = now - cacheItem.timestamp;

          if (age > cacheItem.ttl) {
            localStorage.removeItem(key);
          }
        } catch {
          // Not a cache item, skip
        }
      }
    });
  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = (): { total: number; size: string } => {
  try {
    const keys = Object.keys(localStorage);
    const totalSize = keys.reduce((acc, key) => {
      const item = localStorage.getItem(key);
      return acc + (item ? item.length : 0);
    }, 0);

    return {
      total: keys.length,
      size: `${(totalSize / 1024).toFixed(2)} KB`,
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { total: 0, size: '0 KB' };
  }
};

// Cache keys constants
export const CACHE_KEYS = {
  MOVIES_LIST: 'movies_list',
  BANNER_MOVIES: 'banner_movies',
  POPULAR_MOVIES: 'popular_movies',
  EXCLUSIVE_MOVIES: 'exclusive_movies',
  MOVIE_DETAIL: (id: string) => `movie_detail_${id}`,
  WATCHLIST: 'watchlist',
  USER_PREFERENCES: 'user_preferences',
} as const;

// TTL constants (in minutes)
export const CACHE_TTL = {
  SHORT: 5, // 5 minutes
  MEDIUM: 30, // 30 minutes
  LONG: 60, // 1 hour
  VERY_LONG: 1440, // 24 hours
} as const;
